import { Application, Container, Graphics, Ticker } from 'pixi.js'
import {
  type AdminDebugScenario,
  INITIAL_WIDTH,
  FIELD_RADIUS,
  NETCODE_V4_PROTOCOL_VERSION,
  SnakeNetFlagsV4,
  ServerMsgV4,
} from '@star-snake/shared'
import {
  economicSparksFromSnakeLength,
  snakeCollectionRadiusFromLength,
  snakeSegmentSpacingFromLength,
  snakeTailProgressFromLength,
  snakeWidthFromLength,
} from '@star-snake/shared'
import type { DeathMessage, EvacuationMessage, SessionOverMessage, ViolationWarningMessage } from '@star-snake/shared'
import { GameConnection, NetworkManager } from '../network/NetworkManager'
import { Camera } from './Camera'
import { SnakeRenderer } from './SnakeRenderer'
import type { SnakeData } from './SnakeRenderer'
import { SparkRenderer } from './SparkRenderer'
import { BackgroundRenderer } from './BackgroundRenderer'
import { PortalRenderer } from './PortalRenderer'
import { Minimap } from '../ui/Minimap'
import { AudioManager } from '../audio/AudioManager'
import { ParticleManager } from '../vfx/ParticleManager'
import { VFXOrchestrator } from '../vfx/VFXOrchestrator'
import { DeathScatterRenderer } from '../vfx/DeathScatterRenderer'
import { DeathCamera } from './DeathCamera'
import { SteeringInputV4 } from '../input/SteeringInputV4'
import type { InputState } from '../input/InputTypes'
import { HUD } from '../ui/HUD'
import { ClientPerfObserver } from './ClientPerfObserver'
import { RttMonitor } from '../net/RttMonitor'
import { BinaryDecoderV4 } from '../netcode-v4/protocol/BinaryDecoderV4'
import type { ChainPointV4 } from '../netcode-v4/protocol/BinaryDecoderV4'
import { encodeChainResyncRequestV4, encodePingV4, encodeRespawnRequestV4 } from '../netcode-v4/protocol/ControlEncoderV4'
import { encodeInputV4 } from '../netcode-v4/protocol/InputEncoderV4'
import { ClockSyncV4 } from '../netcode-v4/runtime/ClockSyncV4'
import { SelfChainModel } from '../netcode-v4/runtime/SelfChainModel'
import { RemoteChainBuffer } from '../netcode-v4/runtime/RemoteChainBuffer'
import { SPARK_SIZE } from '@star-snake/shared'
import { TelegramSDK } from '../utils/telegram'
import { getAppViewport, mountToAppShell } from '../ui/appShell'
import { getPlayerColorKey } from './snakeColors'
import { AdminDebugPilot } from '../debug/AdminDebugPilot'
import { consumeAckedInputSentTimes } from './inputAck'
import { loadRenderQuality } from '../config/gameMode'
import {
  createInitialNetworkRecoveryState,
  shouldRequestNetworkResync,
  shouldSendNetworkInput,
  updateNetworkRecoveryState,
  type SentInputFingerprint,
} from './networkRecovery'

const QUALITY_LABEL = 'V4'
const PING_INTERVAL_MS = 2000
const SOFT_DRIFT_RESYNC_SAMPLES = 5

interface RemoteSnakeStateV4 {
  length: number
  flags: number
  spawnToken: number
  lastSeenTick: number
  botBehavior: number
}

interface SparkStateV4 {
  id: string
  x: number
  z: number
  value: number
  color: number
  hidden: boolean
}

interface PendingSparkHideV4 {
  untilTick: number
  x: number
  z: number
}

interface HudLeaderboardEntry {
  entityId: number
  name: string
  score: number
  isMe: boolean
}

interface MinimapSnakeEntry {
  entityId: number
  x: number
  z: number
}

interface RenderSnakeScratch {
  id: string
  x: number
  z: number
  bearing: number
  segments: ChainPointV4[]
  width: number
  segmentSpacing: number
  tailProgress: number
  isAlive: boolean
  isBoosting: boolean
  isInvulnerable: boolean
  isBot: boolean
  botBehavior: number
  skinId: string
  name: string
}

interface GameV4Options {
  fieldRadius?: number
  roomName?: string
  adminDebugEnabled?: boolean
}

export class GameV4 {
  private readonly app: Application
  private readonly connection: GameConnection
  private readonly worldContainer: Container
  private readonly camera: Camera
  private readonly background: BackgroundRenderer
  private readonly snakeRenderer: SnakeRenderer
  private readonly sparkRenderer: SparkRenderer
  private readonly portalRenderer: PortalRenderer
  private readonly minimap: Minimap
  private readonly steeringInput: SteeringInputV4
  private readonly hud: HUD
  private readonly audio: AudioManager
  private readonly decoder = new BinaryDecoderV4()
  private readonly clockSync = new ClockSyncV4()
  private readonly selfModel = new SelfChainModel()
  private readonly remoteBuffers = new Map<number, RemoteChainBuffer>()
  private readonly remoteState = new Map<number, RemoteSnakeStateV4>()
  private readonly sparks = new Map<number, SparkStateV4>()
  private readonly pendingSparkHides = new Map<number, PendingSparkHideV4>()
  private readonly connectionDisposers: Array<() => void> = []
  private readonly rttMonitor = new RttMonitor()
  private readonly perf = new ClientPerfObserver()
  private readonly roomName: string
  private readonly adminDebugPilot: AdminDebugPilot | null
  private readonly hudLeaderboardScratch: HudLeaderboardEntry[] = []
  private readonly minimapSnakeScratch: MinimapSnakeEntry[] = []
  private readonly debugPilotSnakeHeadScratch: Array<{ x: number; z: number }> = []
  private readonly staleRemoteEntityIds: number[] = []
  private readonly selfRenderedSegmentsScratch: ChainPointV4[] = []
  private readonly renderSnakeScratch: RenderSnakeScratch[] = []
  private readonly entityIdStringCache = new Map<number, string>()
  private readonly visibleSparkIdScratch: string[] = []

  private myEntityId = -1
  private myRenderX = 0
  private myRenderZ = 0
  private fps = 0
  private fpsFrames = 0
  private lastFpsTime = performance.now()
  private frameCount = 0
  private currentInput: InputState = { desiredHeading: 0, boost: false, hasAim: false }
  private lastInputSentMs = 0
  private lastSentInputFingerprint: SentInputFingerprint | null = null
  private avgInputSendGapMs = 0
  private maxInputSendGapMs = 0
  private avgTouchToSendAgeMs: number | null = null
  private inputSeq = 0
  private networkRecoveryState = createInitialNetworkRecoveryState()
  private lastAckProgressAtMs = 0
  private lastAutoNetworkResyncAtMs = 0
  private pingTimer: number | null = null
  private disposed = false
  private paused = false
  private dead = false
  private onAssignedSnakeCallback: (() => void) | null = null
  private onDeathCallback: ((data: DeathMessage) => void) | null = null
  private onEvacuationCallback: ((data: EvacuationMessage) => void) | null = null
  private onSessionOverCallback: ((data: SessionOverMessage) => void) | null = null
  private onGameEndCallback: (() => void) | null = null
  private onViolationWarningCallback: ((data: ViolationWarningMessage) => void) | null = null
  private lastAckSeq = -1
  private inputAckMs = 0
  private latestRttMs = 0
  private readonly inputSentAt = new Map<number, number>()
  private latestSnapshotPerf = 0
  private snapshotIntervalMs = 0
  private syncError = 0
  private softDriftSamples = 0
  private latestServerTick = 0
  private latestRenderAlpha = 1
  private gameTimer = 0
  private maxDuration = 1800
  private gamePhase = 'playing'
  private leaderboard: Array<{ entityId: number; name: string; score: number; isBot: boolean }> = []
  private minimapSnakes: Array<{ entityId: number; x: number; z: number; isBot: boolean }> = []
  private minimapRadius = FIELD_RADIUS
  private portalState = { x: 0, z: 0, active: false, radius: 130, relocating: false, timeLeft: 0 }
  private remoteBatchTick = -1
  private readonly remoteBatchActiveIds = new Set<number>()
  private readonly particles: ParticleManager
  private readonly vfx: VFXOrchestrator
  private readonly deathScatter: DeathScatterRenderer
  private readonly deathCamera = new DeathCamera()
  private wasBoosting = false
  private readonly joystickOverlay: Graphics | null = null
  private readonly directionArrow: Graphics | null = null
  private lastMinimapUpdateMs = 0
  private lastMinimapTick = -1
  private lastJoystickActive = false
  private lastJoystickHasAim = false
  private lastJoystickOriginX = Number.NaN
  private lastJoystickOriginY = Number.NaN
  private lastJoystickThumbX = Number.NaN
  private lastJoystickThumbY = Number.NaN
  private lastJoystickHeading = Number.NaN
  private lastJoystickDistancePx = Number.NaN
  private lastPerfUploadMs = 0
  private lastUploadedHeavyFrameAtMs = 0
  private perfUploadPromise: Promise<void> | null = null
  private pendingLongStallUpload = false
  private readonly perfUploadHealth = {
    attemptedCount: 0,
    succeededCount: 0,
    failedCount: 0,
    consecutiveFailureCount: 0,
    lastAttemptAtMs: null as number | null,
    lastSuccessAtMs: null as number | null,
    lastFailureAtMs: null as number | null,
    lastHttpStatus: null as number | null,
    lastError: null as string | null,
  }

  constructor(app: Application, connection: GameConnection, options: GameV4Options = {}) {
    const fieldRadius = options.fieldRadius ?? FIELD_RADIUS
    if (connection.protocolVersion < NETCODE_V4_PROTOCOL_VERSION) {
      throw new Error(`GameV4 requires protocolVersion >= ${NETCODE_V4_PROTOCOL_VERSION}`)
    }

    this.app = app
    this.connection = connection
    this.roomName = options.roomName ?? 'game'
    this.worldContainer = new Container()
    this.app.stage.addChild(this.worldContainer)
    this.camera = new Camera(this.worldContainer)
    this.camera.setFollowLerp(1.0)
    this.background = new BackgroundRenderer(this.worldContainer, fieldRadius)
    this.snakeRenderer = new SnakeRenderer(this.worldContainer)
    this.sparkRenderer = new SparkRenderer(this.worldContainer)
    this.portalRenderer = new PortalRenderer(this.worldContainer)
    this.particles = new ParticleManager(this.worldContainer)
    this.vfx = new VFXOrchestrator(this.camera, this.particles, this.worldContainer)
    this.deathScatter = new DeathScatterRenderer(this.worldContainer, this.camera, this.particles)
    this.portalRenderer.setParticleManager(this.particles)
    this.sparkRenderer.onSparkCollected = (x, z) => {
      this.particles.emit('spark_collect', x, z)
      this.audio.play('collect')
    }
    this.sparkRenderer.onSelfSparkCollected = (value) => {
      TelegramSDK.hapticSparkValue(value)
    }
    this.minimap = new Minimap(fieldRadius)
    this.steeringInput = new SteeringInputV4(
      this.app.canvas as HTMLCanvasElement,
    )
    this.currentInput = this.steeringInput.update()
    const qualityLevel = loadRenderQuality() + 1
    if (this.steeringInput.isTouchMode()) {
      this.snakeRenderer.setQualityLevel(Math.min(qualityLevel, 1))
      this.sparkRenderer.setQualityLevel(Math.min(qualityLevel, 1))
    } else {
      this.snakeRenderer.setQualityLevel(qualityLevel)
      this.sparkRenderer.setQualityLevel(qualityLevel)
    }
    this.adminDebugPilot = options.adminDebugEnabled
      ? new AdminDebugPilot({
          roomName: this.roomName,
          onScenario: (scenario) => this.runAdminDebugScenario(scenario),
          onBenchmarkCompleted: () => this.forceUploadPerf(),
        })
      : null
    this.hud = new HUD()
    this.audio = AudioManager.getInstance()

    if (this.steeringInput.isTouchMode()) {
      this.joystickOverlay = new Graphics()
      this.joystickOverlay.zIndex = 1000
      this.app.stage.addChild(this.joystickOverlay)
      this.directionArrow = new Graphics()
      this.worldContainer.addChild(this.directionArrow)
    }

    this.connectionDisposers.push(this.connection.onBinary((data) => this.handleBinary(data)))
    this.startGameLoop()
    this.startPingLoop()
    this.audio.playMusic('gameplay')
  }

  setOnExit(callback: () => void): void {
    this.hud.setOnExit(callback)
  }

  setOnAssignedSnake(callback: () => void): void {
    this.onAssignedSnakeCallback = callback
  }

  setOnDeath(callback: (data: DeathMessage) => void): void {
    this.onDeathCallback = callback
  }

  setOnEvacuation(callback: (data: EvacuationMessage) => void): void {
    this.onEvacuationCallback = callback
  }

  setOnSessionOver(callback: (data: SessionOverMessage) => void): void {
    this.onSessionOverCallback = callback
  }

  setOnGameEnd(callback: () => void): void {
    this.onGameEndCallback = callback
  }

  setOnViolationWarning(callback: (data: ViolationWarningMessage) => void): void {
    this.onViolationWarningCallback = callback
  }

  requestRespawn(): void {
    this.connection.sendBinary(encodeRespawnRequestV4())
  }

  pause(): void {
    this.paused = true
  }

  resume(): void {
    if (this.disposed) return
    this.paused = false
    this.dead = false
  }

  setDead(): void {
    this.dead = true
    this.paused = true
  }

  setAlive(): void {
    this.dead = false
    this.paused = false
  }

  triggerMapExplosion(): void {
    this.portalState.active = false
    this.vfx.triggerMapExplosion(0, 0, FIELD_RADIUS, this.getAliveSnakePositions())
  }

  getMyEntityId(): number {
    return this.myEntityId
  }

  getPerfUploadHealthSnapshot(): {
    attemptedCount: number
    succeededCount: number
    failedCount: number
    consecutiveFailureCount: number
    lastAttemptAtMs: number | null
    lastSuccessAtMs: number | null
    lastFailureAtMs: number | null
    lastHttpStatus: number | null
    lastError: string | null
  } {
    return { ...this.perfUploadHealth }
  }

  triggerPortalEntry(): void {
    if (this.portalState.active) {
      this.portalRenderer.triggerEntry(this.portalState.x, this.portalState.z)
    }

    // Screen whiteout overlay — CSS-based for reliability across game cleanup
    const whiteout = document.createElement('div')
    whiteout.style.cssText = `
      position: absolute; top: 0; left: 0; right: 0; bottom: 0;
      background: white; opacity: 0; z-index: 500;
      pointer-events: none;
      transition: opacity 0.4s ease-in;
    `
    mountToAppShell(whiteout)
    requestAnimationFrame(() => { whiteout.style.opacity = '0.9' })
    setTimeout(() => {
      whiteout.style.transition = 'opacity 0.6s ease-out'
      whiteout.style.opacity = '0'
      setTimeout(() => whiteout.remove(), 600)
    }, 500)
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.app.ticker.remove(this.gameLoop, this)
    for (const dispose of this.connectionDisposers.splice(0)) dispose()
    this.steeringInput.dispose()
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer)
      this.pingTimer = null
    }
    this.rttMonitor.save()
    this.snakeRenderer.dispose()
    this.sparkRenderer.dispose()
    this.portalRenderer.dispose()
    this.minimap.dispose()
    this.background.dispose()
    this.hud.dispose()
    this.audio.stopMusic()
    this.audio.stop('boost')
    this.deathScatter.dispose()
    this.particles.dispose()
    this.vfx.dispose()
    this.perf.dispose()
    this.adminDebugPilot?.dispose()
    if (this.joystickOverlay) {
      this.app.stage.removeChild(this.joystickOverlay)
      this.joystickOverlay.destroy()
    }
    if (this.directionArrow) {
      this.worldContainer.removeChild(this.directionArrow)
      this.directionArrow.destroy()
    }
    this.app.stage.removeChild(this.worldContainer)
  }

  private startGameLoop(): void {
    this.app.ticker.add(this.gameLoop, this)
  }

  private gameLoop(ticker: Ticker): void {
    if (this.disposed) return

    const frameStartMs = performance.now()
    const dt = ticker.deltaMS / 1000
    this.perf.beginFrame(ticker.deltaMS, frameStartMs)
    this.adminDebugPilot?.tick(frameStartMs)
    this.currentInput = this.steeringInput.update()
    const pilotContext = this.buildDebugPilotContext()
    const pilotInput = pilotContext ? this.adminDebugPilot?.computeInput(pilotContext) : null
    if (pilotInput) {
      this.currentInput = pilotInput
    }
    this.updateFps()

    if (!this.dead && !this.paused && this.selfModel.isInitialized()) {
      const now = performance.now()
      this.updateNetworkRecovery(now)
      const previousHead = this.selfModel.getHead()
      this.selfModel.prepareRenderFrame()
      this.sendInput(this.currentInput, now)
      this.maybeRequestNetworkResync(now)
      this.selfModel.advanceToTick(this.clockSync.getNextInputTick(now, this.selfModel.getCurrentTick()))
      this.latestRenderAlpha = 1
      const currentHead = this.selfModel.getHead()
      this.updatePendingSparkHides(previousHead.x, previousHead.z, currentHead.x, currentHead.z)
    }

    if (this.selfModel.isInitialized()) {
      this.selfModel.updateDisplay()
      const boostHead = this.selfModel.getRenderedHead(1)
      if (Number.isFinite(boostHead.x) && Number.isFinite(boostHead.z)) {
        this.myRenderX = boostHead.x
        this.myRenderZ = boostHead.z
      }
      const isBoosting = (boostHead.flags & SnakeNetFlagsV4.BOOSTING) !== 0
      if (isBoosting && !this.wasBoosting) this.audio.play('boost')
      if (!isBoosting && this.wasBoosting) this.audio.stop('boost')
      this.wasBoosting = isBoosting
    }

    if (this.paused) {
      this.particles.setViewport(this.camera.getViewport())
      const portalStartMs = performance.now()
      this.portalRenderer.update(this.portalState, dt)
      this.perf.record('portalRendererMs', performance.now() - portalStartMs)
      const deathScatterStartMs = performance.now()
      this.deathScatter.update(this.getEffectRenderTick())
      this.perf.record('deathScatterMs', performance.now() - deathScatterStartMs)
      const particlesStartMs = performance.now()
      this.particles.update(dt)
      this.perf.record('particlesMs', performance.now() - particlesStartMs)
      this.vfx.update(dt)
      this.publishPerf(frameStartMs)
      return
    }

    this.updateCamera(dt)
    this.renderSnakes()
    this.selfModel.markRenderFrameDone()
    this.updateJoystickOverlay()
    this.particles.setViewport(this.camera.getViewport())
    const portalStartMs = performance.now()
    this.portalRenderer.update(this.portalState, dt)
    this.perf.record('portalRendererMs', performance.now() - portalStartMs)
    const deathScatterStartMs = performance.now()
    this.deathScatter.update(this.getEffectRenderTick())
    this.perf.record('deathScatterMs', performance.now() - deathScatterStartMs)
    const particlesStartMs = performance.now()
    this.particles.update(dt)
    this.perf.record('particlesMs', performance.now() - particlesStartMs)
    this.vfx.update(dt)
    if (this.frameCount % 15 === 0) {
      const hudStartMs = performance.now()
      this.updateHUD()
      this.perf.record('hudMs', performance.now() - hudStartMs)
    }
    this.publishPerf(frameStartMs)
  }

  private handleBinary(data: ArrayBuffer): void {
    const startMs = performance.now()
    try {
      const msgType = new DataView(data).getUint8(0)

      switch (msgType) {
        case ServerMsgV4.ASSIGN_SNAKE_V3: {
        const assign = this.decoder.decodeAssignSnake(data)
        this.myEntityId = assign.entityId
        this.selfModel.initializeFromAssign(assign)
        this.remoteBuffers.clear()
        this.remoteState.clear()
        this.sparks.clear()
        this.pendingSparkHides.clear()
        this.leaderboard = []
        this.minimapSnakes = []
        this.syncError = 0
        this.softDriftSamples = 0
        this.lastAckSeq = -1
        this.inputAckMs = 0
        this.latestRttMs = 0
        this.inputSentAt.clear()
        this.lastInputSentMs = 0
        this.lastSentInputFingerprint = null
        this.networkRecoveryState = createInitialNetworkRecoveryState()
        this.lastAckProgressAtMs = 0
        this.lastAutoNetworkResyncAtMs = 0
        this.remoteBatchTick = -1
        this.remoteBatchActiveIds.clear()
        this.deathScatter.clear()
        this.myRenderX = assign.x
        this.myRenderZ = assign.z
        this.camera.setTarget(assign.x, assign.z)
        this.camera.setZoomForLength(assign.length)
        this.camera.snapToTarget()
        // Use menu color seed so in-game snake matches the preview
        const menuColorSeed = parseInt(localStorage.getItem('star-snake-color-seed') || '0', 10) || 0
        this.snakeRenderer.setColorSeedOverride(String(assign.entityId), getPlayerColorKey(menuColorSeed))
        this.particles.emit('spawn_materialize', assign.x, assign.z)
        this.particles.emit('spawn_scatter', assign.x, assign.z)
        this.audio.play('respawn')
        this.deathCamera.reset()
        this.onAssignedSnakeCallback?.()
          break
        }

        case ServerMsgV4.SELF_HEAD_STATE_V3: {
        const state = this.decoder.decodeSelfHeadState(data)
        this.onOwnerSyncSample(state.serverTick, state.serverMonoMs)
        this.processAck(state.ackSeq)
        const reconcile = this.selfModel.applySelfHeadState(state)
        this.syncError = reconcile.headError
        if (reconcile.kind === 'soft-drift') {
          this.softDriftSamples++
        } else {
          this.softDriftSamples = 0
        }
        if (reconcile.kind === 'resync' || this.softDriftSamples >= SOFT_DRIFT_RESYNC_SAMPLES) {
          this.selfModel.snapDisplay()
          this.connection.sendBinary(encodeChainResyncRequestV4(1, this.latestServerTick))
          this.softDriftSamples = 0
        }
          break
        }

        case ServerMsgV4.SELF_CHAIN_SNAPSHOT_V3: {
        const snapshot = this.decoder.decodeSelfChainSnapshot(data)
        this.onOwnerSyncSample(snapshot.serverTick, snapshot.serverMonoMs)
        this.processAck(snapshot.ackSeq)
        this.selfModel.applySelfChainSnapshot(snapshot)
        this.selfModel.snapDisplay()
        this.syncError = 0
        this.softDriftSamples = 0
          break
        }

        case ServerMsgV4.REMOTE_CHAIN_BATCH_V3: {
        const batch = this.decoder.decodeRemoteChainBatch(data)
        this.noteServerTick(batch.serverTick)
        this.beginRemoteBatchTick(batch.serverTick)
        for (const snake of batch.snakes) {
          this.remoteBatchActiveIds.add(snake.entityId)
          this.remoteState.set(snake.entityId, {
            length: snake.length,
            flags: snake.flags,
            spawnToken: snake.spawnToken,
            lastSeenTick: batch.serverTick,
            botBehavior: snake.botBehavior,
          })
          let buffer = this.remoteBuffers.get(snake.entityId)
          if (!buffer) {
            buffer = new RemoteChainBuffer()
            this.remoteBuffers.set(snake.entityId, buffer)
          }
          buffer.pushSnapshot({ ...snake, serverTick: batch.serverTick })
        }
          break
        }

        case ServerMsgV4.PONG_V3: {
        const pong = this.decoder.decodePong(data)
        const now = performance.now()
        this.clockSync.onPong(pong.clientMonoMs, pong.serverMonoMs, now)
          this.latestRttMs = this.clockSync.getRttMsFromPong(pong.clientMonoMs, now)
          this.rttMonitor.record(this.latestRttMs)
          break
        }

        case ServerMsgV4.SPARK_BATCH_V3: {
        const batch = this.decoder.decodeSparkBatch(data)
        this.noteServerTick(batch.serverTick)
        for (const spark of batch.sparks) {
          const pending = this.pendingSparkHides.get(spark.sparkId)
          const keepHidden = pending !== undefined && Math.hypot(pending.x - spark.x, pending.z - spark.z) <= 8
          this.sparks.set(spark.sparkId, {
            id: String(spark.sparkId),
            x: spark.x,
            z: spark.z,
            value: spark.value,
            color: 0,
            hidden: keepHidden,
          })
          if (!keepHidden) this.pendingSparkHides.delete(spark.sparkId)
        }
          break
        }

        case ServerMsgV4.SPARK_REMOVE_V3: {
        const removed = this.decoder.decodeSparkRemove(data)
        this.noteServerTick(removed.serverTick)
        for (const sparkId of removed.sparkIds) {
          this.sparks.delete(sparkId)
          this.pendingSparkHides.delete(sparkId)
        }
          break
        }

        case ServerMsgV4.DEATH_SCATTER_V1: {
        const scatter = this.decoder.decodeDeathScatter(data)
        this.noteServerTick(scatter.serverTick)
        const deathGhost = this.buildSnakeSnapshotForDeath(scatter.sourceEntityId)
        if (deathGhost) {
          this.snakeRenderer.triggerDeathGhost(deathGhost)
        }
        this.deathScatter.addBundles(scatter.bundles, scatter.serverTick, this.getEffectRenderTick())
          break
        }

        case ServerMsgV4.GAME_STATE_V3: {
        const state = this.decoder.decodeGameState(data)
        this.noteServerTick(state.serverTick)
        this.gameTimer = state.timer
        this.maxDuration = state.maxDuration
        this.gamePhase = state.phase
        this.selfModel.setMovementConfig(state.runtimeConfig)
        this.leaderboard = state.leaderboard
        this.minimapSnakes = state.minimap.snakes
        this.minimapRadius = state.minimap.radius
        this.portalState = state.portal
          break
        }

        case ServerMsgV4.DEATH_V3: {
        const death = this.decoder.decodeDeath(data)
        this.noteServerTick(death.serverTick)
        if (this.selfModel.isInitialized()) {
          const deathHead = this.selfModel.getRenderedHead(1)
          this.particles.emit('death_explosion', deathHead.x, deathHead.z)
          this.particles.emit('death_sparks', deathHead.x, deathHead.z)
          this.camera.shake(20, 0.8)
          this.deathCamera.start(this.camera.getZoom())
        }
        this.audio.play('death')
        this.audio.stop('boost')
        this.wasBoosting = false
        this.onDeathCallback?.(death)
          break
        }

        case ServerMsgV4.EVACUATION_V3: {
        const evacuation = this.decoder.decodeEvacuation(data)
        this.noteServerTick(evacuation.serverTick)
        this.audio.play('portal_enter')
        this.onEvacuationCallback?.(evacuation)
          break
        }

        case ServerMsgV4.SESSION_OVER_V3: {
        const sessionOver = this.decoder.decodeSessionOver(data)
        this.noteServerTick(sessionOver.serverTick)
        this.vfx.triggerMapExplosion(0, 0, FIELD_RADIUS, this.getAliveSnakePositions())
        this.onSessionOverCallback?.(sessionOver)
          break
        }

        case ServerMsgV4.GAME_END_V3: {
        const gameEnd = this.decoder.decodeGameEnd(data)
        this.noteServerTick(gameEnd.serverTick)
        this.onGameEndCallback?.()
          break
        }

        case ServerMsgV4.VIOLATION_WARNING_V3: {
        const warning = this.decoder.decodeViolationWarning(data)
        this.noteServerTick(warning.serverTick)
        this.onViolationWarningCallback?.(warning)
          break
        }
      }
    } finally {
      this.perf.record('handleBinaryMs', performance.now() - startMs)
    }
  }

  private onOwnerSyncSample(serverTick: number, serverMonoMs: number): void {
    this.clockSync.onServerSample(serverTick, serverMonoMs)
    this.noteServerTick(serverTick)
    this.flushCompletedRemoteBatchTick()
    if (this.clockSync.consumeResyncRequest()) {
      this.connection.sendBinary(encodeChainResyncRequestV4(2, this.latestServerTick))
    }
    const now = performance.now()
    if (this.latestSnapshotPerf > 0) {
      const dtMs = now - this.latestSnapshotPerf
      this.snapshotIntervalMs = this.snapshotIntervalMs === 0 ? dtMs : this.snapshotIntervalMs * 0.8 + dtMs * 0.2
    }
    this.latestSnapshotPerf = now
  }

  private noteServerTick(serverTick: number): void {
    this.latestServerTick = Math.max(this.latestServerTick, serverTick >>> 0)
    this.clockSync.noteServerTick(serverTick)
  }

  private getEffectRenderTick(): number {
    return this.clockSync.shouldHoldNewest() ? this.latestServerTick : this.clockSync.getRenderTick()
  }

  private processAck(ackSeq: number): void {
    if (ackSeq === this.lastAckSeq) return
    const now = performance.now()
    this.lastAckSeq = ackSeq
    this.lastAckProgressAtMs = now
    const sentAt = consumeAckedInputSentTimes(this.inputSentAt, ackSeq)
    if (sentAt === undefined) return
    const rtt = Math.max(0, now - sentAt)
    this.inputAckMs = this.inputAckMs === 0 ? rtt : this.inputAckMs * 0.8 + rtt * 0.2
  }

  private sendInput(input: InputState, now = performance.now()): void {
    const head = this.selfModel.getHead()
    const desiredHeading = input.hasAim
      ? input.desiredHeading
      : head.bearing
    const nextInput: SentInputFingerprint = {
      hasAim: input.hasAim,
      desiredHeading,
      boost: input.boost,
    }
    if (!shouldSendNetworkInput({
      nowMs: now,
      lastSentAtMs: this.lastInputSentMs,
      mode: this.networkRecoveryState.mode,
      nextInput,
      lastSentInput: this.lastSentInputFingerprint,
    })) {
      return
    }

    if (this.lastInputSentMs > 0) {
      const gapMs = Math.max(0, now - this.lastInputSentMs)
      this.avgInputSendGapMs = this.avgInputSendGapMs === 0
        ? gapMs
        : this.avgInputSendGapMs * 0.8 + gapMs * 0.2
      this.maxInputSendGapMs = Math.max(this.maxInputSendGapMs, gapMs)
    }
    const touchToSendAgeMs = this.steeringInput.getTouchInput()?.getLastMoveAgeMs(now) ?? null
    if (touchToSendAgeMs !== null) {
      this.avgTouchToSendAgeMs = this.avgTouchToSendAgeMs === null
        ? touchToSendAgeMs
        : this.avgTouchToSendAgeMs * 0.8 + touchToSendAgeMs * 0.2
    }
    const seq = this.inputSeq & 0xFFFF
    const inputTick = this.clockSync.getNextInputTick(now, this.selfModel.getCurrentTick())

    this.connection.sendBinary(encodeInputV4({
      seq,
      inputTick,
      desiredHeading,
      boost: input.boost,
    }))

    this.selfModel.recordInput({
      seq,
      inputTick,
      desiredHeading,
      flags: input.boost ? 0x01 : 0x00,
    })
    this.inputSentAt.set(seq, now)
    if (this.inputSentAt.size > 512) {
      const cutoff = now - 15000
      for (const [key, ts] of this.inputSentAt) {
        if (ts < cutoff) this.inputSentAt.delete(key)
      }
    }

    this.inputSeq++
    this.lastInputSentMs = now
    this.lastSentInputFingerprint = nextInput
  }

  private updateNetworkRecovery(nowMs: number): void {
    this.networkRecoveryState = updateNetworkRecoveryState(this.networkRecoveryState, {
      rttMs: this.latestRttMs,
      inputAckMs: this.inputAckMs,
      pendingInputAckCount: this.inputSentAt.size,
    }, nowMs)
  }

  private maybeRequestNetworkResync(nowMs: number): void {
    const shouldResync = shouldRequestNetworkResync({
      nowMs,
      mode: this.networkRecoveryState.mode,
      latestServerTick: this.latestServerTick,
      lastAckProgressAtMs: this.lastAckProgressAtMs,
      lastAutoResyncAtMs: this.lastAutoNetworkResyncAtMs,
      sample: {
        rttMs: this.latestRttMs,
        inputAckMs: this.inputAckMs,
        pendingInputAckCount: this.inputSentAt.size,
      },
    })
    if (!shouldResync) return

    this.connection.sendBinary(encodeChainResyncRequestV4(4, this.latestServerTick))
    this.connection.sendBinary(encodePingV4(Math.floor(nowMs)))
    this.lastAutoNetworkResyncAtMs = nowMs
  }

  private renderSnakes(): void {
    const renderStartMs = performance.now()
    let snakeCount = 0

    if (this.selfModel.isInitialized() && this.myEntityId >= 0) {
      const head = this.selfModel.getRenderedHead(this.latestRenderAlpha)
      const segments = this.selfModel.copyRenderedChainTo(this.selfRenderedSegmentsScratch)
      this.myRenderX = head.x
      this.myRenderZ = head.z
      const snake = this.getRenderSnakeScratch(snakeCount++)
      snake.id = this.getEntityIdString(this.myEntityId)
      snake.x = head.x
      snake.z = head.z
      snake.bearing = head.bearing
      snake.segments = segments
      snake.width = snakeWidthFromLength(head.length)
      snake.segmentSpacing = snakeSegmentSpacingFromLength(head.length)
      snake.tailProgress = snakeTailProgressFromLength(head.length)
      snake.isAlive = true
      snake.isBoosting = (head.flags & SnakeNetFlagsV4.BOOSTING) !== 0
      snake.isInvulnerable = (head.flags & SnakeNetFlagsV4.INVULNERABLE) !== 0
      snake.isBot = false
      snake.botBehavior = 0
      snake.skinId = 'default'
      snake.name = ''
    }

    const renderTick = this.clockSync.shouldHoldNewest() ? Number.POSITIVE_INFINITY : this.clockSync.getRenderTick()
    for (const [entityId, buffer] of this.remoteBuffers) {
      const rendered = buffer.getRendered(renderTick)
      if (!rendered) continue
      const state = this.remoteState.get(entityId)
      const flags = state?.flags ?? rendered.flags
      const snake = this.getRenderSnakeScratch(snakeCount++)
      snake.id = this.getEntityIdString(entityId)
      snake.x = rendered.points[0]?.x ?? 0
      snake.z = rendered.points[0]?.z ?? 0
      snake.bearing = rendered.bearing
      snake.segments = rendered.points
      snake.width = snakeWidthFromLength(rendered.length)
      snake.segmentSpacing = snakeSegmentSpacingFromLength(rendered.length)
      snake.tailProgress = snakeTailProgressFromLength(rendered.length)
      snake.isAlive = true
      snake.isBoosting = (flags & SnakeNetFlagsV4.BOOSTING) !== 0
      snake.isInvulnerable = (flags & SnakeNetFlagsV4.INVULNERABLE) !== 0
      snake.isBot = (flags & SnakeNetFlagsV4.BOT) !== 0
      snake.botBehavior = state?.botBehavior ?? 0
      snake.skinId = snake.isBot ? '10' : 'default'
      snake.name = snake.isBot ? `Bot-${entityId}` : ''
    }
    this.renderSnakeScratch.length = snakeCount

    const snakeRendererStartMs = performance.now()
    this.snakeRenderer.update(
      this.renderSnakeScratch,
      this.myRenderX,
      this.myRenderZ,
      this.myEntityId >= 0 ? String(this.myEntityId) : null,
      this.camera.getViewport(),
    )
    this.perf.record('snakeRendererMs', performance.now() - snakeRendererStartMs)
    this.restoreExpiredPendingSparkHides()
    const sparkStartMs = performance.now()
    this.sparkRenderer.update(this.sparks.values(), this.app.ticker.deltaMS / 1000, this.camera.getViewport())
    this.perf.record('sparkRendererMs', performance.now() - sparkStartMs)
    const minimapStartMs = performance.now()
    this.updateMinimap()
    this.perf.record('minimapMs', performance.now() - minimapStartMs)
    this.perf.record('renderSnakesMs', performance.now() - renderStartMs)
  }

  private updateCamera(dt: number): void {
    if (this.selfModel.isInitialized()) {
      this.camera.setTarget(this.myRenderX, this.myRenderZ)
      this.camera.setZoomForLength(this.selfModel.getHead().length)
    }
    this.camera.update(dt)
    if (this.dead) {
      const dcState = this.deathCamera.update(dt)
      if (dcState.active) {
        this.camera.setFollowLerp(1.0)
      }
    }
  }

  private updateJoystickOverlay(): void {
    const touch = this.steeringInput.getTouchInput()
    if (!touch || !this.joystickOverlay || !this.directionArrow) return

    if (!touch.isActive()) {
      if (this.lastJoystickActive || this.directionArrow.visible) {
        this.joystickOverlay.clear()
        this.directionArrow.clear()
        this.directionArrow.visible = false
      }
      this.lastJoystickActive = false
      this.lastJoystickHasAim = false
      return
    }

    const origin = touch.getOrigin()
    const thumb = touch.getThumbPosition()
    const dx = thumb.x - origin.x
    const dy = thumb.y - origin.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    const heading = this.currentInput.desiredHeading
    const distancePx = this.currentInput.distancePx ?? 60
    const overlayChanged =
      !this.lastJoystickActive
      || this.lastJoystickHasAim !== this.currentInput.hasAim
      || Math.abs(origin.x - this.lastJoystickOriginX) > 0.5
      || Math.abs(origin.y - this.lastJoystickOriginY) > 0.5
      || Math.abs(thumb.x - this.lastJoystickThumbX) > 0.5
      || Math.abs(thumb.y - this.lastJoystickThumbY) > 0.5
      || Math.abs(distancePx - this.lastJoystickDistancePx) > 0.5

    if (overlayChanged) {
      this.joystickOverlay.clear()
      this.lastJoystickActive = true
      this.lastJoystickHasAim = this.currentInput.hasAim
      this.lastJoystickOriginX = origin.x
      this.lastJoystickOriginY = origin.y
      this.lastJoystickThumbX = thumb.x
      this.lastJoystickThumbY = thumb.y
      this.lastJoystickDistancePx = distancePx

      // Draw joystick overlay in screen space
      const joystickRadius = 60
      this.joystickOverlay.circle(origin.x, origin.y, joystickRadius)
      this.joystickOverlay.fill({ color: 0xffffff, alpha: 0.08 })
      this.joystickOverlay.stroke({ color: 0xffffff, alpha: 0.15, width: 2 })

      const clampedDist = Math.min(dist, joystickRadius)
      const angle = Math.atan2(dy, dx)
      const thumbScreenX = origin.x + Math.cos(angle) * clampedDist
      const thumbScreenY = origin.y + Math.sin(angle) * clampedDist
      this.joystickOverlay.circle(thumbScreenX, thumbScreenY, 14)
      this.joystickOverlay.fill({ color: 0xffffff, alpha: 0.2 })
    }

    // Draw direction arrow in world space near snake head
    this.directionArrow.clear()
    if (!this.selfModel.isInitialized() || !this.currentInput.hasAim) {
      this.directionArrow.visible = false
      return
    }

    this.directionArrow.visible = true
    this.lastJoystickActive = true
    this.lastJoystickHasAim = this.currentInput.hasAim
    this.lastJoystickHeading = heading
    const head = this.selfModel.getRenderedHead(1)
    // Arrow distance matches finger distance from joystick center (screen px → world units)
    const zoom = this.camera.getZoom()
    const arrowDist = Math.max(INITIAL_WIDTH * 1.2, distancePx / zoom)
    const arrowX = head.x + Math.cos(heading) * arrowDist
    const arrowZ = head.z + Math.sin(heading) * arrowDist

    // Laser pointer dot — bright red with halo glow
    const dotRadius = INITIAL_WIDTH * 0.2
    // Wide soft halo
    this.directionArrow.circle(arrowX, arrowZ, dotRadius * 4)
    this.directionArrow.fill({ color: 0xFF0000, alpha: 0.06 })
    // Mid halo
    this.directionArrow.circle(arrowX, arrowZ, dotRadius * 2.5)
    this.directionArrow.fill({ color: 0xFF0000, alpha: 0.12 })
    // Inner glow
    this.directionArrow.circle(arrowX, arrowZ, dotRadius * 1.5)
    this.directionArrow.fill({ color: 0xFF1111, alpha: 0.25 })
    // Core dot
    this.directionArrow.circle(arrowX, arrowZ, dotRadius)
    this.directionArrow.fill({ color: 0xFF0000, alpha: 0.9 })
    // Hot center
    this.directionArrow.circle(arrowX, arrowZ, dotRadius * 0.4)
    this.directionArrow.fill({ color: 0xFF4444, alpha: 1.0 })
    // White-hot center
    this.directionArrow.circle(arrowX, arrowZ, dotRadius * 0.15)
    this.directionArrow.fill({ color: 0xFFAAAA, alpha: 1.0 })
  }

  private startPingLoop(): void {
    if (this.pingTimer !== null) return
    this.connection.sendBinary(encodePingV4(Math.floor(performance.now())))
    this.pingTimer = window.setInterval(() => {
      this.connection.sendBinary(encodePingV4(Math.floor(performance.now())))
    }, PING_INTERVAL_MS)
  }

  private updateFps(): void {
    this.frameCount++
    this.fpsFrames++
    const now = performance.now()
    const elapsed = now - this.lastFpsTime
    if (elapsed >= 1000) {
      this.fps = (this.fpsFrames * 1000) / elapsed
      this.fpsFrames = 0
      this.lastFpsTime = now
    }
  }

  private updateHUD(): void {
    const head = this.selfModel.getHead()
    const dx = this.portalState.x - this.myRenderX
    const dz = this.portalState.z - this.myRenderZ
    const viewport = getAppViewport()
    this.hudLeaderboardScratch.length = this.leaderboard.length
    for (let i = 0; i < this.leaderboard.length; i++) {
      const entry = this.leaderboard[i]
      let scratch = this.hudLeaderboardScratch[i]
      if (!scratch) {
        scratch = { entityId: 0, name: '', score: 0, isMe: false }
        this.hudLeaderboardScratch[i] = scratch
      }
      scratch.entityId = entry.entityId
      scratch.name = entry.name
      scratch.score = entry.score
      scratch.isMe = entry.entityId === this.myEntityId
    }
    this.hud.update({
      leaderboard: this.hudLeaderboardScratch,
      timer: this.gameTimer,
      maxDuration: this.maxDuration,
      myScore: economicSparksFromSnakeLength(head.length),
      myLength: economicSparksFromSnakeLength(head.length),
      phase: this.gamePhase,
      portalX: this.portalState.x,
      portalZ: this.portalState.z,
      portalActive: this.portalState.active,
      cameraX: this.myRenderX,
      cameraZ: this.myRenderZ,
      viewportWidth: viewport.width / this.camera.getZoom(),
      viewportHeight: viewport.height / this.camera.getZoom(),
      portalDistance: Math.hypot(dx, dz),
      portalTimeLeft: this.portalState.timeLeft,
    })
  }

  private updatePendingSparkHides(ax: number, az: number, bx: number, bz: number): void {
    const radius = snakeCollectionRadiusFromLength(this.selfModel.getHead().length) + SPARK_SIZE * 0.5
    const radiusSq = radius * radius
    const untilTick = this.latestServerTick + 10
    const minX = Math.min(ax, bx) - radius
    const maxX = Math.max(ax, bx) + radius
    const minZ = Math.min(az, bz) - radius
    const maxZ = Math.max(az, bz) + radius
    const candidateSparkIds = this.sparkRenderer.copyActiveSparkIdsTo(this.visibleSparkIdScratch)

    if (candidateSparkIds.length <= 0) {
      for (const [sparkId, spark] of this.sparks) {
        if (spark.hidden) continue
        if (spark.x < minX || spark.x > maxX || spark.z < minZ || spark.z > maxZ) continue
        if (distSqPointToSegment(spark.x, spark.z, ax, az, bx, bz) > radiusSq) continue
        spark.hidden = true
        this.pendingSparkHides.set(sparkId, { untilTick, x: spark.x, z: spark.z })
      }
      return
    }

    for (let i = 0; i < candidateSparkIds.length; i++) {
      const sparkId = Number(candidateSparkIds[i])
      const spark = this.sparks.get(sparkId)
      if (!spark) continue
      if (spark.hidden) continue
      if (spark.x < minX || spark.x > maxX || spark.z < minZ || spark.z > maxZ) continue
      if (distSqPointToSegment(spark.x, spark.z, ax, az, bx, bz) > radiusSq) continue
      spark.hidden = true
      this.pendingSparkHides.set(sparkId, { untilTick, x: spark.x, z: spark.z })
    }
  }

  private restoreExpiredPendingSparkHides(): void {
    for (const [sparkId, pending] of this.pendingSparkHides) {
      if (pending.untilTick > this.latestServerTick) continue
      const spark = this.sparks.get(sparkId)
      if (spark) spark.hidden = false
      this.pendingSparkHides.delete(sparkId)
    }
  }

  private updateMinimap(): void {
    const now = performance.now()
    const shouldUpdate =
      this.latestServerTick !== this.lastMinimapTick
      || now - this.lastMinimapUpdateMs >= 120
      || this.frameCount % 6 === 0

    if (!shouldUpdate) return

    let count = 0
    for (const snake of this.minimapSnakes) {
      if (snake.entityId === this.myEntityId) continue
      let scratch = this.minimapSnakeScratch[count]
      if (!scratch) {
        scratch = { entityId: 0, x: 0, z: 0 }
        this.minimapSnakeScratch[count] = scratch
      }
      scratch.entityId = snake.entityId
      scratch.x = snake.x
      scratch.z = snake.z
      count++
    }
    this.minimapSnakeScratch.length = count

    const dx = this.portalState.x - this.myRenderX
    const dz = this.portalState.z - this.myRenderZ
    const portalDistance = Math.hypot(dx, dz)
    const head = this.selfModel.getHead()

    this.minimap.updateFromServer({
      tick: this.latestServerTick,
      snakes: this.minimapSnakeScratch,
      portalActive: this.portalState.active,
      portalInRange: portalDistance <= this.minimapRadius,
      portalX: this.portalState.x,
      portalZ: this.portalState.z,
      portalAngle: Math.atan2(dz, dx),
    }, this.myRenderX, this.myRenderZ, FIELD_RADIUS, this.hud.getScoreLabels(), economicSparksFromSnakeLength(head.length), this.gameTimer, this.maxDuration)
    this.lastMinimapTick = this.latestServerTick
    this.lastMinimapUpdateMs = now
  }

  private beginRemoteBatchTick(serverTick: number): void {
    if (this.remoteBatchTick === serverTick) return
    this.flushCompletedRemoteBatchTick()
    this.remoteBatchTick = serverTick
    this.remoteBatchActiveIds.clear()
  }

  private getAliveSnakePositions(): Array<{ x: number; z: number }> {
    const positions: Array<{ x: number; z: number }> = []
    const renderTick = this.clockSync.shouldHoldNewest() ? Number.POSITIVE_INFINITY : this.clockSync.getRenderTick()
    for (const [, buffer] of this.remoteBuffers) {
      const rendered = buffer.getRendered(renderTick)
      if (rendered?.points[0]) positions.push(rendered.points[0])
    }
    if (this.selfModel.isInitialized()) {
      const head = this.selfModel.getHead()
      positions.push({ x: head.x, z: head.z })
    }
    return positions
  }

  private flushCompletedRemoteBatchTick(): void {
    if (this.remoteBatchTick < 0 || this.latestServerTick <= this.remoteBatchTick) return

    const renderTick = this.clockSync.shouldHoldNewest() ? Number.POSITIVE_INFINITY : this.clockSync.getRenderTick()
    this.staleRemoteEntityIds.length = 0
    for (const entityId of this.remoteBuffers.keys()) {
      if (this.remoteBatchActiveIds.has(entityId)) continue
      this.staleRemoteEntityIds.push(entityId)
    }

    for (const entityId of this.staleRemoteEntityIds) {
      const buffer = this.remoteBuffers.get(entityId)
      const rendered = buffer?.getRendered(renderTick)
      if (rendered && this.portalState.active) {
        const head = rendered.points[0]
        if (head) {
          const distanceToPortal = Math.hypot(head.x - this.portalState.x, head.z - this.portalState.z)
          if (distanceToPortal <= Math.max(150, this.portalState.radius * 1.85)) {
            const flags = this.remoteState.get(entityId)?.flags ?? rendered.flags
            this.portalRenderer.triggerEntry(head.x, head.z)
            this.snakeRenderer.triggerPortalFade({
              id: String(entityId),
              x: head.x,
              z: head.z,
              bearing: rendered.bearing,
              segments: rendered.points.map((point) => ({ x: point.x, z: point.z })),
              width: snakeWidthFromLength(rendered.length),
              segmentSpacing: snakeSegmentSpacingFromLength(rendered.length),
              tailProgress: snakeTailProgressFromLength(rendered.length),
              isAlive: false,
              isBoosting: (flags & SnakeNetFlagsV4.BOOSTING) !== 0,
              isInvulnerable: (flags & SnakeNetFlagsV4.INVULNERABLE) !== 0,
              isBot: (flags & SnakeNetFlagsV4.BOT) !== 0,
              botBehavior: this.remoteState.get(entityId)?.botBehavior ?? 0,
              skinId: (flags & SnakeNetFlagsV4.BOT) !== 0 ? '10' : 'default',
              name: '',
            })
          }
        }
      }
      this.remoteBuffers.delete(entityId)
      this.remoteState.delete(entityId)
    }

    this.remoteBatchTick = -1
    this.remoteBatchActiveIds.clear()
    this.staleRemoteEntityIds.length = 0
  }

  private publishPerf(nowMs: number): void {
    const pendingBinaryStats = this.connection.getPendingBinaryStats()
    const touchInputActive = this.steeringInput.getTouchInput()?.isActive() ?? false
    const touchMoveGapMs = this.steeringInput.getTouchInput()?.getAverageMoveGapMs() ?? null
    const appState = (window as Window & { __STAR_SNAKE_APP_STATE__?: string }).__STAR_SNAKE_APP_STATE__ ?? 'unknown'
    this.perf.setCounts({
      sparkStateCount: this.sparks.size,
      sparkSpriteCount: this.sparkRenderer.getActiveSpriteCount(),
      remoteSnakeCount: this.remoteBuffers.size,
      snakeGraphicsCount: this.snakeRenderer.getActiveGraphicsCount(),
      portalFadeCount: this.snakeRenderer.getActivePortalFadeCount(),
      particleCount: this.particles.getActiveParticleCount(),
      deathScatterCount: this.deathScatter.getActiveShardCount(),
      pendingBinaryCount: pendingBinaryStats.queued,
      droppedPendingBinaryCount: pendingBinaryStats.dropped,
    })
    this.perf.endFrame(Math.max(0, performance.now() - nowMs))
    const frameDtMs = this.app.ticker.deltaMS
    this.perf.captureFrame(nowMs, frameDtMs)
    if (this.perf.isLongStall(frameDtMs)) {
      this.perf.recordLongStall({
        updatedAtMs: nowMs,
        stallDtMs: frameDtMs,
        appState,
        gamePhase: this.gamePhase,
        documentHidden: document.hidden,
        paused: this.paused,
        dead: this.dead,
        touchInputActive,
        touchHasAim: touchInputActive && this.currentInput.hasAim,
        pendingBinaryCount: pendingBinaryStats.queued,
        pendingInputAckCount: this.inputSentAt.size,
        wsBufferedAmount: pendingBinaryStats.bufferedAmount,
        rttMs: this.latestRttMs,
        inputAckMs: this.inputAckMs,
      })
      this.pendingLongStallUpload = true
    }
    this.perf.publish(nowMs, this.fps, this.snapshotIntervalMs, {
      inputAckMs: this.inputAckMs,
      rttMs: this.latestRttMs,
      avgAckOverRttMs: Math.max(0, this.inputAckMs - this.latestRttMs),
      avgInputSendGapMs: this.avgInputSendGapMs,
      maxInputSendGapMs: this.maxInputSendGapMs,
      avgTouchToSendAgeMs: this.avgTouchToSendAgeMs,
      avgTouchMoveGapMs: touchMoveGapMs,
      pendingInputAckCount: this.inputSentAt.size,
      wsBufferedAmount: pendingBinaryStats.bufferedAmount,
    })
    void this.maybeUploadPerf(nowMs, this.pendingLongStallUpload)
  }

  private getRenderSnakeScratch(index: number): RenderSnakeScratch {
    let snake = this.renderSnakeScratch[index]
    if (!snake) {
      snake = {
        id: '',
        x: 0,
        z: 0,
        bearing: 0,
        segments: [],
        width: 0,
        segmentSpacing: 0,
        tailProgress: 0,
        isAlive: true,
        isBoosting: false,
        isInvulnerable: false,
        isBot: false,
        botBehavior: 0,
        skinId: 'default',
        name: '',
      }
      this.renderSnakeScratch[index] = snake
    }
    return snake
  }

  private getEntityIdString(entityId: number): string {
    let cached = this.entityIdStringCache.get(entityId)
    if (!cached) {
      cached = String(entityId)
      this.entityIdStringCache.set(entityId, cached)
    }
    return cached
  }

  private buildSnakeSnapshotForDeath(entityId: number): SnakeData | null {
    if (entityId < 0) return null

    if (entityId === this.myEntityId && this.selfModel.isInitialized()) {
      const head = this.selfModel.getRenderedHead(this.latestRenderAlpha)
      const segments = this.selfModel.getRenderedChain(this.latestRenderAlpha)
      return {
        id: String(entityId),
        x: head.x,
        z: head.z,
        bearing: head.bearing,
        segments,
        width: snakeWidthFromLength(head.length),
        segmentSpacing: snakeSegmentSpacingFromLength(head.length),
        tailProgress: snakeTailProgressFromLength(head.length),
        isAlive: false,
        isBoosting: (head.flags & SnakeNetFlagsV4.BOOSTING) !== 0,
        isInvulnerable: (head.flags & SnakeNetFlagsV4.INVULNERABLE) !== 0,
        isBot: false,
        skinId: 'default',
        name: '',
      }
    }

    const buffer = this.remoteBuffers.get(entityId)
    if (!buffer) return null
    const renderTick = this.clockSync.shouldHoldNewest() ? Number.POSITIVE_INFINITY : this.clockSync.getRenderTick()
    const rendered = buffer.getRendered(renderTick)
    if (!rendered || rendered.points.length <= 0) return null

    const flags = this.remoteState.get(entityId)?.flags ?? rendered.flags
    return {
      id: String(entityId),
      x: rendered.points[0]?.x ?? 0,
      z: rendered.points[0]?.z ?? 0,
      bearing: rendered.bearing,
      segments: rendered.points.map((point) => ({ x: point.x, z: point.z })),
      width: snakeWidthFromLength(rendered.length),
      segmentSpacing: snakeSegmentSpacingFromLength(rendered.length),
      tailProgress: snakeTailProgressFromLength(rendered.length),
      isAlive: false,
      isBoosting: (flags & SnakeNetFlagsV4.BOOSTING) !== 0,
      isInvulnerable: (flags & SnakeNetFlagsV4.INVULNERABLE) !== 0,
      isBot: (flags & SnakeNetFlagsV4.BOT) !== 0,
      skinId: (flags & SnakeNetFlagsV4.BOT) !== 0 ? '10' : 'default',
      name: (flags & SnakeNetFlagsV4.BOT) !== 0 ? `Bot-${entityId}` : '',
    }
  }

  private buildDebugPilotContext(): {
    x: number
    z: number
    bearing: number
    fieldRadius: number
    portal: { x: number; z: number; active: boolean }
    sparks: Iterable<{ x: number; z: number; hidden?: boolean }>
    snakeHeads: Iterable<{ x: number; z: number }>
  } | null {
    if (!this.selfModel.isInitialized()) return null
    const head = this.selfModel.getHead()
    let snakeHeadCount = 0
    for (let i = 0; i < this.minimapSnakes.length; i++) {
      const snake = this.minimapSnakes[i]
      if (snake.entityId === this.myEntityId) continue
      let scratch = this.debugPilotSnakeHeadScratch[snakeHeadCount]
      if (!scratch) {
        scratch = { x: 0, z: 0 }
        this.debugPilotSnakeHeadScratch[snakeHeadCount] = scratch
      }
      scratch.x = snake.x
      scratch.z = snake.z
      snakeHeadCount++
    }
    this.debugPilotSnakeHeadScratch.length = snakeHeadCount
    return {
      x: head.x,
      z: head.z,
      bearing: head.bearing,
      fieldRadius: FIELD_RADIUS,
      portal: {
        x: this.portalState.x,
        z: this.portalState.z,
        active: this.portalState.active,
      },
      sparks: this.sparks.values(),
      snakeHeads: this.debugPilotSnakeHeadScratch,
    }
  }

  private maybeUploadPerf(nowMs: number, force = false): Promise<void> {
    const network = NetworkManager.getInstance()
    if (!network.isAdmin) return Promise.resolve()
    const snapshot = this.perf.getLatestSnapshot()
    if (!snapshot) return Promise.resolve()

    if (this.perfUploadPromise) {
      if (!force) return this.perfUploadPromise
      return this.perfUploadPromise.then(() => this.maybeUploadPerf(nowMs, true))
    }

    const latestHeavyFrameAtMs = snapshot.heavyFrames[snapshot.heavyFrames.length - 1]?.updatedAtMs ?? 0
    const heavyFramePending = latestHeavyFrameAtMs > this.lastUploadedHeavyFrameAtMs
    const benchmark = this.adminDebugPilot?.getBenchmarkInfo() ?? {
      sessionId: null,
      active: false,
      phaseKey: null,
      phaseLabel: null,
      phaseIndex: null,
      phaseCount: null,
    }
    const uploadIntervalMs = benchmark.active ? 2000 : (heavyFramePending ? 1500 : 5000)
    if (!force && nowMs - this.lastPerfUploadMs < uploadIntervalMs) return Promise.resolve()

    const viewport = getAppViewport()
    const head = this.selfModel.isInitialized() ? this.selfModel.getHead() : null
    const portalDistance = head
      ? Math.hypot(this.portalState.x - head.x, this.portalState.z - head.z)
      : null
    const touchInputActive = this.steeringInput.getTouchInput()?.isActive() ?? false
    const touchInputAgeMs = this.steeringInput.getTouchInput()?.getActiveTouchAgeMs() ?? null
    const touchMoveAgeMs = this.steeringInput.getTouchInput()?.getLastMoveAgeMs() ?? null
    const touchStaleRecoveryCount = this.steeringInput.getTouchInput()?.getStaleRecoveryCount() ?? 0
    const appState = (window as Window & { __STAR_SNAKE_APP_STATE__?: string }).__STAR_SNAKE_APP_STATE__ ?? 'unknown'

    this.perfUploadHealth.attemptedCount++
    this.perfUploadHealth.lastAttemptAtMs = nowMs

    this.lastPerfUploadMs = nowMs

    const uploadPromise = network.uploadClientPerf({
      clientSessionId: network.clientDebugSessionId,
      roomName: this.roomName,
      snapshot,
      device: {
        viewportWidth: viewport.width,
        viewportHeight: viewport.height,
        devicePixelRatio: window.devicePixelRatio || 1,
        touchMode: this.steeringInput.isTouchMode(),
        userAgent: navigator.userAgent.slice(0, 500),
      },
      game: {
        phase: this.gamePhase,
        playerEntityId: this.myEntityId >= 0 ? this.myEntityId : null,
        portalDistance,
        paused: this.paused,
        dead: this.dead,
      },
      ui: {
        touchInputActive,
        touchHasAim: touchInputActive && this.currentInput.hasAim,
        touchInputAgeMs,
        touchMoveAgeMs,
        touchStaleRecoveryCount,
        benchmarkVisible: this.adminDebugPilot?.isBenchmarkVisible() ?? false,
        documentHidden: document.hidden,
        appState,
      },
      benchmark,
      debugPilot: {
        active: this.adminDebugPilot?.isActive() ?? false,
        mode: this.adminDebugPilot?.getMode() ?? 'off',
      },
      telemetry: {
        ...this.perfUploadHealth,
      },
    })
      .then((result) => {
        this.perfUploadHealth.succeededCount++
        this.perfUploadHealth.consecutiveFailureCount = 0
        this.perfUploadHealth.lastSuccessAtMs = nowMs
        this.perfUploadHealth.lastHttpStatus = result?.status ?? 200
        this.perfUploadHealth.lastError = null
        this.lastUploadedHeavyFrameAtMs = latestHeavyFrameAtMs
        this.pendingLongStallUpload = false
      })
      .catch((error: unknown) => {
        this.perfUploadHealth.failedCount++
        this.perfUploadHealth.consecutiveFailureCount++
        this.perfUploadHealth.lastFailureAtMs = nowMs
        const message = error instanceof Error ? error.message : String(error)
        this.perfUploadHealth.lastError = message.slice(0, 240)
        const statusMatch = /\bHTTP (\d{3})\b/.exec(message)
        this.perfUploadHealth.lastHttpStatus = statusMatch
          ? Number.parseInt(statusMatch[1] ?? '', 10)
          : null
      })
      .finally(() => {
        if (this.perfUploadPromise === uploadPromise) {
          this.perfUploadPromise = null
        }
      })

    this.perfUploadPromise = uploadPromise
    return uploadPromise
  }

  private forceUploadPerf(): Promise<void> {
    return this.maybeUploadPerf(performance.now(), true)
  }

  private async runAdminDebugScenario(scenario: AdminDebugScenario): Promise<void> {
    await NetworkManager.getInstance().runAdminRoomDebugScenario(this.roomName, scenario)
  }
}

function distSqPointToSegment(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const abx = bx - ax
  const abz = bz - az
  const apx = px - ax
  const apz = pz - az
  const abLenSq = abx * abx + abz * abz
  if (abLenSq <= 0.0001) {
    const dx = px - ax
    const dz = pz - az
    return dx * dx + dz * dz
  }

  const t = Math.max(0, Math.min(1, (apx * abx + apz * abz) / abLenSq))
  const cx = ax + abx * t
  const cz = az + abz * t
  const dx = px - cx
  const dz = pz - cz
  return dx * dx + dz * dz
}
